import { DataCache } from '.';
import { eventBus, CollectionType, ErrorType, LinkType, PreferenceType } from '../../internal';
import { clone, deepFreeze } from '../../util';
import { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from '../Axios';
import { IJqXhr, IPrefCacheHelper, JqXhrAdapter, PrefCacheHelper, SERVICE_URL } from './prefHelper';

const FILTER_REGEX_APP_EQ_FIRST_PART = /^eq\s*\(\s*(\"|\')?application(\"|\')?\s*,/;
const FILTER_REGEX_APP_EQ = /^eq\s*\(\s*(\"|\')?application(\"|\')?\s*,.*\)$/;
const FILTER_REGEX_ID_IN_FIRST_PART = /^in\s*\(\s*(\"|\')?id(\"|\')?\s*,/;
const FILTER_REGEX_ID_IN = /^in\s*\(\s*(\"|\')?id(\"|\')?\s*,.*\)$/;
const UNSUPPORTED_FILTERFNS_REGEX = /.*(and|or|not)\(.*/;
const DEFAULT_LIMIT = 20;

/**
 * Helper function to find pref by given id
 */
function findById(id: string, prefData: CollectionType<PreferenceType>) {
  let res;
  if (prefData.items) {
    res = prefData.items.find((item) => item.id === id);
  }
  return res;
}

type FilterFunction = {
  (item: PreferenceType): boolean;
};
/**
 * @function
 * @private
 */
const getFilterFunction = (filterRawVal: string): FilterFunction | undefined => {
  filterRawVal = decodeURI(filterRawVal);
  const eqFilter = FILTER_REGEX_APP_EQ.test(filterRawVal);
  const inFilter = FILTER_REGEX_ID_IN.test(filterRawVal);

  if (!eqFilter && !inFilter) {
    return;
  }
  const mapFilterVal = (item: string) => {
    if (item.charAt(0) === "'" || item.charAt(0) === '"') {
      return item.substring(1, item.length - 1);
    }
    return item;
  };

  let arrFilterVals: Array<string>;
  // eq(application,val1,val2): application is equal to input values.
  if (eqFilter) {
    filterRawVal = filterRawVal.replace(FILTER_REGEX_APP_EQ_FIRST_PART, '');
    filterRawVal = filterRawVal.substring(0, filterRawVal.length - 1);
    if (filterRawVal.length < 1 || UNSUPPORTED_FILTERFNS_REGEX.test(filterRawVal)) {
      return;
    }
    arrFilterVals = filterRawVal.split(',').map(mapFilterVal);
    return function (item: PreferenceType): boolean {
      let match = false;
      arrFilterVals.every((appName) => {
        if (new Intl.Collator().compare(appName, item.application ?? '') === 0) {
          match = true;
          return false;
        }
        return true;
      });
      return match;
    };
  }
  // in(id,val1,val2) filter: id in input values.
  if (inFilter) {
    filterRawVal = filterRawVal.replace(FILTER_REGEX_ID_IN_FIRST_PART, '');
    filterRawVal = filterRawVal.substring(0, filterRawVal.length - 1);
    if (filterRawVal.length < 1 || UNSUPPORTED_FILTERFNS_REGEX.test(filterRawVal)) {
      return;
    }
    arrFilterVals = filterRawVal.split(',').map(mapFilterVal);
    return function (item: PreferenceType): boolean {
      let match = false;
      arrFilterVals.every((id) => {
        if (new Intl.Collator().compare(id, item.id) === 0) {
          match = true;
          return false;
        }
        return true;
      });
      return match;
    };
  }
};

/**
 * @function
 * @private
 */
const selfLink = (
  target: Array<LinkType>,
  removeQueryParams?: boolean
): { self: LinkType; selfIndex: number } | undefined => {
  let res: { self: LinkType; selfIndex: number } | undefined;
  target.every((link: LinkType, index) => {
    if (link.rel === 'self' && link.method.toUpperCase() === 'GET') {
      res = {
        self: link,
        selfIndex: index,
      };
      return false;
    }
    return true;
  });
  if (!!removeQueryParams && res) {
    res.self.uri = new URL(res.self.uri, self.location.origin).pathname;
    res.self.href = res.self.uri;
  }
  return res;
};

/**
 * Object proxy handler for filtered preferences cached data object.
 * The data object is used as target for returning a proxy when filter query
 * parameter is specified while fetching user preferences.
 *
 * Filters out data based on input.
 * @private
 */
class FilteredDataProxyHandler implements ProxyHandler<CollectionType<PreferenceType>> {
  private filter: string;
  private filterFn: FilterFunction;
  private items?: Array<PreferenceType>;
  private links?: Array<LinkType>;

  constructor(f: string, fn: FilterFunction) {
    this.filter = f;
    this.filterFn = fn;
  }

  // Getter trap for the target.
  get(target: CollectionType<PreferenceType>, property: PropertyKey, _: any): any {
    // first filter data.
    if (!this.items) {
      this.items = deepFreeze(target.items.filter(this.filterFn));
    }

    if (property in target) {
      switch (property) {
        case 'count': {
          return this.items!.length;
        }
        case 'links': {
          if (!this.links) {
            const linksCp: Array<any> = target.links!.slice();
            const selfData = selfLink(linksCp);
            if (selfData) {
              linksCp.splice(selfData.selfIndex, 1);
              const selfUri = `${selfData.self.uri}?filter=${this.filter}`;
              const lnObj = clone(selfData.self);
              lnObj.uri = selfUri;
              lnObj.href = selfUri;
              linksCp.push(lnObj);
            }
            this.links = linksCp;
          }
          return this.links;
        }
        default: {
          return deepFreeze((target as any)[property as string]);
        }
      }
    }
  }
  // setter trap
  set(
    _target: CollectionType<PreferenceType>,
    _p: PropertyKey,
    _value: any,
    _receiver: any
  ): boolean {
    // read only
    return false;
  }
}

function appendQuery(_uri: string, _start: number, _limit: number): string {
  let query = _uri.indexOf('?') === -1 ? '?' : '&';
  query += `start=${_start}`;
  query += `&limit=${_limit}`;
  return _uri + query;
}

/**
 * Object proxy handler for preferences cached data object.
 *
 * Adds pagination support on data based on input.
 * @private
 */
class PaginationDataProxyHandler implements ProxyHandler<CollectionType<PreferenceType>> {
  private start = 0;
  private limit: number = DEFAULT_LIMIT;
  private items?: Array<PreferenceType>;
  private links?: Array<LinkType>;

  constructor(start: string | null, limit: string | null) {
    if (start !== null) {
      this.start = parseInt(start);
    }
    if (limit !== null) {
      this.limit = parseInt(limit);
    }
  }
  // Getter trap for the target.
  get(target: CollectionType<PreferenceType>, property: PropertyKey, _: any): any {
    if (!this.items) {
      this.items = deepFreeze(
        target.items.slice(this.start, Math.min(this.start + this.limit, target.count!))
      );
    }
    if (property in target) {
      switch (property) {
        case 'count': {
          return this.items!.length;
        }
        case 'items': {
          return this.items;
        }
        case 'links': {
          if (!this.links && target.links && target.count) {
            const linksCp: Array<any> = target.links.slice();
            const selfData = selfLink(linksCp);
            if (selfData) {
              const self_link = selfData.self;
              const _uri = self_link.uri;
              linksCp.splice(selfData.selfIndex, 1);
              const selfUri = appendQuery(_uri, this.start, this.limit);
              const lnObj = clone(self_link);
              lnObj.uri = selfUri;
              lnObj.href = selfUri;
              linksCp.push(lnObj);
            }

            // add next,prev,first and last links if applicable.
            if (this.start > 0 && selfData) {
              const self_link = selfData.self;
              const _uri = self_link.uri;
              // populate link for first.
              const firstUri = appendQuery(_uri, 0, this.limit);
              const flnObj = clone(self_link);
              flnObj.uri = firstUri;
              flnObj.href = firstUri;
              flnObj.rel = 'first';
              linksCp.push(flnObj);

              const prevUri = appendQuery(_uri, Math.max(0, this.start - this.limit), this.limit);
              const plnObj = clone(self_link);
              plnObj.uri = prevUri;
              plnObj.href = prevUri;
              plnObj.rel = 'prev';
              linksCp.push(plnObj);
            }
            if (selfData && this.start + this.limit < target.count && this.limit < target.count) {
              const self_link = selfData.self;
              const _uri = self_link.uri;
              // last
              const lastUri = appendQuery(
                _uri,
                target.count - (this.limit - Math.ceil(target.count / this.limit)),
                this.limit
              );
              const llnObj = clone(self_link);
              llnObj.uri = lastUri;
              llnObj.href = lastUri;
              llnObj.rel = 'last';
              linksCp.push(llnObj);

              const nextUri = appendQuery(_uri, this.start + this.limit, this.limit);
              const nlnObj = clone(self_link);
              nlnObj.uri = nextUri;
              nlnObj.href = nextUri;
              nlnObj.rel = 'next';
              linksCp.push(nlnObj);
            }
            this.links = deepFreeze(linksCp);
          }
          return this.links;
        }
        default: {
          return deepFreeze((target as any)[property as string]);
        }
      }
    }
  }

  // setter trap
  set(
    _target: CollectionType<PreferenceType>,
    _p: PropertyKey,
    _value: any,
    _receiver: any
  ): boolean {
    // read only
    return false;
  }
}

/**
 * Object proxy handler for AxiosError instance returned by cache
 * on error to get a given user preference.
 * @private
 */
// TODO do we need this? Skip for now.
// class ErrorProxyHandler implements ProxyHandler<any>{

//   constructor(public prefId:string){}

//   // Getter trap for the target.
//   get(target: any, property: PropertyKey, _: any):any{
//     if(property in target){
//       if(property === 'message'){
//         return target.message.replace(ERRONEOUS_PREF_ID,this.prefId);
//       }
//       return target[property];
//     }
//   }

//   // setter trap
//   set(target: any, p: PropertyKey, value: any, receiver: any): boolean{
//     // read only
//     return false;
//   }
// }

// UserPrefCache is an in memory cache to hold on to user preferences. Meant to
// help application performance by reducing REST calls to preferences service.
export class UserPrefCache implements DataCache {
  private helper?: PrefCacheHelper;

  // must match with that of openui-framework.
  readonly name: string = 'sas.PreferencesServiceCache';

  constructor(private _axios: AxiosInstance) {
    // TODO grab from top frame if available.
    this.helper = this.getCacheHelper();

    // reload won't be available in helper provided by openui-framework.
    if ('reload' in this.helper!) {
      // subscribe to a reload event to trigger cache reload.
      // eslint-disable-next-line @sas/i18n/jsx-to-localize
      eventBus.subscribe('Cache.Reload', () => this.helper!.reload());
      // subscribe to app show event to trigger cache reload.
      eventBus.subscribe('AppSwitcher.AppShow', () => this.helper!.reload());
    }
  }

  // getCacheHelper returns cache helper that can be reused in app switcher
  // environment if available. Returns a new helper otherwise.
  getCacheHelper(): PrefCacheHelper {
    try {
      const _top: any = window.top;
      if (window !== _top && _top.sas && _top.sas.cacheManager) {
        const topPrefCache = _top.sas.cacheManager.getCache(this.name);
        if (topPrefCache && '__helper' in topPrefCache) {
          return topPrefCache.__helper;
        }
      }
    } catch (err) {
      // security error if apps are cross origin.
      // ignore.
    }
    return new PrefCacheHelper(this._axios);
  }

  // __helper returns helper instance for backward compatibility with openUI apps.
  get __helper(): IPrefCacheHelper | undefined {
    return this.helper;
  }

  canHandle(conf: AxiosRequestConfig): boolean {
    if (
      !conf.url ||
      conf.method === undefined ||
      (conf.responseType && conf.responseType !== 'json') ||
      !this.helper
    ) {
      return false;
    }
    return this.helper!.instanceUrlRegEx.test(conf.url!);
  }

  // invoke makes network call to invoke service.
  private invoke(conf: AxiosRequestConfig): Promise<any> {
    const result = this._axios(conf);
    if (conf.method!.toUpperCase() === 'PUT') {
      return result.then((response: AxiosResponse) => {
        this.helper!.addToCache(response.data, '', new JqXhrAdapter(response));
        return response;
      });
    }
    if (conf.method!.toUpperCase() === 'DELETE') {
      return result.then((response: AxiosResponse) => {
        this.helper!.removeFromCache(response.config);
        return response;
      });
    }
    return result;
  }

  handle<T = any, R = AxiosResponse<T>>(conf: AxiosRequestConfig): Promise<R> {
    if (!this.canHandle(conf)) {
      return this.invoke(conf);
    }

    if (conf.method!.toUpperCase() !== 'GET') {
      return this.invoke(conf);
    }

    if (!this.helper!.initialized()) {
      const retObj = this.invoke(conf);
      retObj.finally(() => this.helper!.init());
      return retObj;
    }

    const toResponse = (data: any): AxiosResponse<T> => {
      const axiosRes: AxiosResponse<T> = {
        config: conf,
        data: data as T,
        headers: this.helper!.cachedJqXhr!.getAllResponseHeaders(),
        status: +this.helper!.cachedJqXhr!.status,
        statusText: this.helper!.cachedJqXhr!.statusText,
      };
      return axiosRes;
    };

    // do this stuff in promise to deal with async nature of call.
    const parsedUri = new URL(conf.url!, self.location.origin);
    // check if the call matches exactly with service url
    // all preferences
    if (
      parsedUri.pathname === SERVICE_URL ||
      parsedUri.pathname === this.helper!.alternateServiceUrl
    ) {
      // try reading all query params.
      const uriParams = parsedUri.searchParams;
      if (conf.params !== undefined) {
        for (const key in conf.params) {
          if (Object.prototype.hasOwnProperty.call(conf.params, key)) {
            uriParams.set(key, conf.params[key]);
          }
        }
      }

      let cachedData = this.helper!.cachedData!;

      // see if data/query has sortBy param.
      if (uriParams.get('sortBy') !== null) {
        // TODO add sorting support using browser collator api.
        // don't bother handling sorting.
        return this.invoke(conf);
      }
      if (uriParams.get('filter') !== null) {
        const filtVal = uriParams.get('filter')!;
        // check if specified filter is supported
        const filterFn = getFilterFunction(filtVal);
        if (filterFn) {
          cachedData = new Proxy<CollectionType<PreferenceType>>(
            this.helper!.cachedData!,
            new FilteredDataProxyHandler(filtVal, filterFn)
          );
        } else {
          return this.invoke(conf);
        }
      }
      const respObj = new Proxy(
        cachedData,
        new PaginationDataProxyHandler(uriParams.get('start'), uriParams.get('limit'))
      );
      return Promise.resolve<R>((toResponse(respObj) as unknown) as R);
    }
    // get specific preference if requested.
    const prefId = this.helper!.extractPrefId(parsedUri.pathname);
    if (prefId) {
      const prefObj = findById(prefId, this.helper!.cachedData!);
      if (prefObj) {
        // found
        return Promise.resolve<R>((toResponse(deepFreeze(prefObj)) as unknown) as R);
      }
    }
    // not found.
    const respObj: IJqXhr = this.helper!.cachedErrObjects![0] as IJqXhr;
    // TODO next release: Proxy error object to return correct message with
    // corrected pref id in it.
    const axErr: AxiosError<ErrorType> = {
      config: conf,
      name: 'error',
      isAxiosError: false,
      message: JSON.stringify(respObj.responseJSON),
      code: '404',
      toJSON: (): object => {
        return respObj.responseJSON;
      },
    };
    return Promise.reject(axErr);
  }
}
